import React, { useMemo } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import RxGhgAccordion from "./RxGhgAccordion";
import { DEFAULT_UNIT } from "../constants";

export default function DonorAllocationsPanel({
  donorRowsFiltered = [],
  donorSelection = [],
  setDonorSelection = () => {},

  // Used to persist edits back to the real rows
  setDonorRows = () => {},

  donorRowCount = 0,
  donorSaving = false,
  donorTotals = { totalQty: 0, totalAvoided: 0 },

  addDonorRow = () => {},
  deleteDonorRow = () => {},
  saveDonorRows = () => {},

  computeAvoidedEmissions = () => "",
  unitLabel = DEFAULT_UNIT,
}) {
  const { t } = useTranslation();

  const donorColumns = useMemo(
    () => [
      {
        field: "row_id",
        headerName: t("ghg.donorAllocations.columns.index", "#"),
        width: 70,
      },
      {
        field: "donor",
        headerName: t("ghg.donorAllocations.columns.donor", "Donor"),
        flex: 1,
        editable: false,
      },
      {
        field: "material_type",
        headerName: t("ghg.donorAllocations.columns.material", "Material"),
        flex: 1,
        editable: false,
      },
      {
        field: "description",
        headerName: t("ghg.donorAllocations.columns.description", "Description"),
        flex: 1,
        editable: true,
      },
      {
        field: "qty",
        headerName: t("ghg.donorAllocations.columns.quantity", "Quantity"),
        flex: 1,
        editable: true,
      },
      {
        field: "avoided_emissions",
        headerName: t("ghg.donorAllocations.columns.avoided", "Avoided ({{unit}})", {
          unit: unitLabel,
        }),
        flex: 1,
        editable: false,
      },
    ],
    [t, unitLabel]
  );

  return (
    <Box className="rxghg_block">
      <RxGhgAccordion
        id="rxghg_donor"
        title={t("ghg.donorAllocations.title", "Donor allocations")}
        subtitle={t(
          "ghg.donorAllocations.subtitle",
          "Computed avoided emissions (delivered requests)"
        )}
        rightEl={
          <Box className="rxghg_accBadges">
            <span className="rxghg_badge">
              {t("ghg.common.rowsCount", "{{count}} rows", { count: donorRowCount })}
            </span>

            <span className="rxghg_badge">
              {t("ghg.donorAllocations.badges.totalAvoided", "Total avoided: {{value}} {{unit}}", {
                value: Number(donorTotals?.totalAvoided || 0).toFixed(2),
                unit: unitLabel,
              })}
            </span>
          </Box>
        }
      >
        <Box className="rxghg_gridWrap">
          <DataGrid
            rows={donorRowsFiltered || []}
            columns={donorColumns || []}
            getRowId={(r) => r.row_id}
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={(ids) => setDonorSelection(ids)}
            processRowUpdate={(newRow) => {
              // Recompute avoided emissions when qty/desc changes
              const updatedRow = {
                ...newRow,
                avoided_emissions: computeAvoidedEmissions(
                  newRow.material_type,
                  newRow.qty
                ),
              };

              setDonorRows((prev) =>
                (prev || []).map((r) =>
                  r.row_id === updatedRow.row_id ? updatedRow : r
                )
              );
              return updatedRow;
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
          <Card variant="outlined" sx={{ minWidth: 220 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {t("ghg.donorAllocations.cards.totalQuantity", "Total quantity")}
              </Typography>
              <Typography variant="h6">
                {Number(donorTotals?.totalQty || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ minWidth: 220 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {t(
                  "ghg.donorAllocations.cards.totalAvoided",
                  "Total avoided emissions ({{unit}})",
                  { unit: unitLabel }
                )}
              </Typography>
              <Typography variant="h6">
                {Number(donorTotals?.totalAvoided || 0).toFixed(6)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Stack direction="row" spacing={1} className="rxghg_rowActions">
          <Button
            className="rxghg_btn"
            variant="contained"
            size="small"
            onClick={addDonorRow}
          >
            {t("ghg.common.actions.add", "Add")}
          </Button>

          <Button
            className="rxghg_btn rxghg_btnGhost"
            variant="outlined"
            size="small"
            onClick={deleteDonorRow}
            disabled={(donorSelection || []).length === 0}
          >
            {t("ghg.common.actions.delete", "Delete")}
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            className="rxghg_btn"
            variant="contained"
            size="small"
            onClick={saveDonorRows}
            disabled={donorSaving}
          >
            {donorSaving
              ? t("ghg.common.actions.saving", "Saving…")
              : t("ghg.common.actions.save", "Save")}
          </Button>
        </Stack>

        <Typography
          variant="caption"
          sx={{ opacity: 0.7, mt: 1, display: "block" }}
        >
          {t(
            "ghg.donorAllocations.caption",
            "Avoided emissions = quantity × (EF − RE) using the current receiver factors."
          )}
        </Typography>
      </RxGhgAccordion>
    </Box>
  );
}